import { CORPUS, corpusEntry, VARIATION_ENTRIES } from '../../data/chorales/corpusIndex';
import { LETTER_RETURN, RETURN_WORDS, ReturnKind } from '../../data/returns';
import { keyName, parseKey } from '../../music/key';
import { usePrefs } from '../hooks';
import { ReturnsMini, ReturnSwatch } from './Returns';
import { CompareTabs } from './Compare';

const KINDS: ReturnKind[] = ['same', 'moved', 'decorated', 'changed', 'missing'];

/** Share of the theme's bars whose harmony comes back (as it was, moved, or under a new surface). */
function kept(rows: Array<{ strip: string }>): number {
  let all = 0;
  let hit = 0;
  for (const r of rows)
    for (const ch of r.strip) {
      all++;
      if (['same', 'moved', 'decorated'].includes(LETTER_RETURN[ch])) hit++;
    }
  return all ? hit / all : 0;
}

/**
 * The theme-and-variations sets (TAVERN): every set as a picture of its variations under the
 * theme, bar by bar, grouped by composer. Each opens the room where the bars can be heard.
 */
export function VariationsIndex() {
  const { prefs } = usePrefs();
  const sets = CORPUS.returns.filter((r) => corpusEntry(r.id)?.kind === 'variations');
  const totals = { same: 0, moved: 0, decorated: 0, changed: 0, missing: 0 } as Record<ReturnKind, number>;
  sets.forEach((r) => r.rows.forEach((row) => [...row.strip].forEach((ch) => totals[LETTER_RETURN[ch]]++)));
  const variations = sets.reduce((a, r) => a + r.rows.length, 0);
  const featured = sets.find((r) => r.id === 'k265');
  const byComposer = ['Mozart', 'Beethoven'].map((c) => ({ composer: c, sets: sets.filter((r) => corpusEntry(r.id)?.composer === c) }));

  if (!sets.length) {
    return (
      <div className="page-head">
        <span className="label">Theme and variations</span>
        <h1>Not in this edition</h1>
        <p>
          <a href="#library">See all pieces</a>
        </p>
      </div>
    );
  }

  return (
    <div>
      <header className="page-head">
        <span className="label">Compare · {VARIATION_ENTRIES.length} sets, {variations} variations</span>
        <h1>The same harmony, new clothes</h1>
        <p>
          A set of variations plays a tune again and again, each time dressed differently: running notes, the tune in the bass, the whole thing in minor. Underneath,
          the harmony mostly stays: across these {sets.length} sets by Mozart and Beethoven, {Math.round(kept(sets.flatMap((r) => r.rows)) * 100)}% of the theme’s bars
          keep it.
        </p>
        <p className="hint">Each picture has one row per variation and one cell per bar of the theme. The colours are explained below.</p>
      </header>
      <CompareTabs current="variations" />

      {featured && (
        <a className="enter" href={`#returns.${featured.id}`} style={{ marginTop: 0, marginBottom: 18 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <span className="label">Start here</span>
            <h2>{corpusEntry(featured.id)?.title}</h2>
            <p>
              Mozart, {corpusEntry(featured.id)?.catalogue}. The tune known in English as “Twinkle, Twinkle, Little Star”, and twelve ways of saying it again. Pick any bar
              and hear it in every variation, one after another.
            </p>
            <div style={{ maxWidth: 420, marginTop: 10 }}>
              <ReturnsMini rows={featured.rows} width={420} />
            </div>
          </div>
        </a>
      )}

      <div className="form-legend" role="list" aria-label="Legend" style={{ marginBottom: 8 }}>
        {KINDS.filter((k) => totals[k] > 0).map((k) => (
          <span key={k} role="listitem">
            <ReturnSwatch kind={k} /> {prefs.depth >= 2 ? RETURN_WORDS[k].name : RETURN_WORDS[k].word}
          </span>
        ))}
      </div>

      {byComposer.map(
        ({ composer, sets: list }) =>
          list.length > 0 && (
            <section key={composer} className="family">
              <span className="label">
                {composer} · {list.length} {list.length === 1 ? 'set' : 'sets'}
              </span>
              <div className="path-grid">
                {list.map((r) => {
                  const e = corpusEntry(r.id)!;
                  return (
                    <a key={r.id} className="path-card" href={`#returns.${r.id}`}>
                      <h3 style={{ fontSize: '1.05rem' }}>{e.title}</h3>
                      <span className="muted" style={{ fontSize: '0.8rem' }}>
                        {e.catalogue} · {keyName(parseKey(e.homeKey))} · {r.rows.length} variations · harmony kept in {Math.round(kept(r.rows) * 100)}% of bars
                      </span>
                      <span className="glyph" style={{ display: 'block' }}>
                        <ReturnsMini rows={r.rows} />
                      </span>
                    </a>
                  );
                })}
              </div>
            </section>
          ),
      )}
      <p className="foot">
        Scores, phrases and Roman numerals: TAVERN, Theme And Variation Encodings with Roman Numerals (Johanna Devaney, Claire Arthur, Nathaniel Condit-Schultz and
        Kirsten Nisula, 2015; CC BY-SA 4.0), annotator A. Each phrase is placed in the score by its notes; see the Method page.
      </p>
    </div>
  );
}
